/**
 * The monsters' view of the level, as the C64 code sees it: the collision map at $8500 (40 bytes a
 * row, bit 7 = solid) read through a cell pointer computed from the sprite position (D_E9B8).
 * Porting the pointer and its byte offsets verbatim keeps every tile test exactly the original's.
 */

import { COLS, ROWS, solidTile } from '../playfield';
import type { Monster, World } from '../types';

/** The map is 40 bytes wide (the screen's width): columns 32..39 are the HUD panel. */
const MAP_WIDTH = 40;

/**
 * What D_E9B8 leaves behind for one sprite position: the cell pointer (as a row and a column, the
 * pointer being map + 40 * row + col - 1) and the sub-cell offsets $23 (x) and $24 (y).
 */
export interface Probe {
  /** Top tile row of the body. */
  row: number;
  /** Tile column of the body's left edge. */
  col: number;
  /** (X - $14) & 7 [$23]: 0 when the body sits on the column grid. */
  subX: number;
  /** (Y - $15) & 7 [$24]: 0 when the body sits on the row grid. */
  subY: number;
}

/** D_E9B8: the cell pointer for a sprite at (x, y), 8-bit arithmetic as on the C64. */
export function probeAt(x: number, y: number): Probe {
  const dx = (x - 0x14) & 0xff;
  const dy = (y - 0x15) & 0xff;
  // Row table $AC03 starts three rows above the screen: entry k is row k - 3.
  return { row: (dy >> 3) - 3, col: dx >> 3, subX: dx & 7, subY: dy & 7 };
}

export const probe = (m: Monster): Probe => probeAt(m.x, m.y);

/**
 * Is the map byte at `off` bytes past the cell pointer solid? Rows -3..-1 hold copies of row 0 and
 * rows 25..27 copies of row 24 (D_3A6C); further out is clamped to those. The HUD columns 32..39 are
 * taken as open [U: the C64 marks HUD characters $10-$1D solid too, $397A].
 */
export function hit(world: World, p: Probe, off: number): boolean {
  const lin = p.row * MAP_WIDTH + p.col - 1 + off;
  const row = Math.floor(lin / MAP_WIDTH);
  const col = lin - row * MAP_WIDTH;
  if (col >= COLS) return false;
  return solidTile(world.playfield, col, Math.max(0, Math.min(ROWS - 1, row)));
}

/** Any of the two (three when off the column grid) cells `off`, `off+1`, `off+2` solid (the $51/$79 tests). */
export function hitRow(world: World, p: Probe, off: number): boolean {
  return hit(world, p, off) || hit(world, p, off + 1) || (p.subX !== 0 && hit(world, p, off + 2));
}

/**
 * L_EA20 (walkers) / L_1F61 (Banebou): a platform top in the body's second column 2..5 rows above
 * its floor - a solid tile with an open one above it, among rows r-4..r (pointer - $A0, offsets
 * 2, $2A, $52, $7A, $A2).
 */
export function platformAbove(world: World, p: Probe): boolean {
  const c = (off: number): boolean => hit(world, p, off - 0xa0);
  return (!c(0x02) && c(0x2a)) || (!c(0x2a) && c(0x52)) || (!c(0x52) && c(0x7a)) || (!c(0x7a) && c(0xa2));
}

/** Byte offsets from the cell pointer: +40 per row down, +1 per column right, from column col - 1. */
export const OFF = {
  /** Row r+1 (the lower half of the body), one column left / right of the body. */
  lowLeft: 0x28,
  lowRight: 0x2b,
  /** Row r+2 (under the feet; beside the body when off the row grid), column col - 1 / col + 2. */
  feetLeft: 0x50,
  feetRight: 0x53,
  /** Row r+2, columns col.. (the floor), and row r+3 (under the feet one row further down). */
  floor: 0x51,
  below: 0x79,
} as const;
